import type { Id } from "@repo/convex/dataModel";
import { ChevronDown, ChevronRight, ExternalLink, RotateCcw, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { MessageMarkdown } from "@/components/ui/message-markdown";
import { useCancelConsultation, useRetryConsultationSetup } from "@/features/clarification/hooks";
import { getErrorMessage } from "@/lib/app-errors";
import { ConsultationStatusBadge } from "./consultation-status-badge";
import { ConsultationTranscript } from "./consultation-transcript";
import type { ConsultationView } from "./consultation-types";
import { TeamsIcon } from "./teams-icon";

function formatEntryTime(date: Date) {
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function inviteeSummary(consultation: ConsultationView) {
  return consultation.inviteeNames.join(", ") || "the team";
}

/**
 * Anchored "invited X to a Teams consultation" log line (Pencil node NJSPe).
 * Stays at its send-time position in the timeline as a permanent record marker.
 */
export function ConsultationStartedEntry({ consultation }: { consultation: ConsultationView }) {
  return (
    <div
      role="note"
      aria-label="Teams consultation started"
      className="border-border flex items-center gap-1.5 border-t px-4 py-2"
    >
      <TeamsIcon size={13} />
      <span className="text-[10px] text-muted-foreground">
        {consultation.requesterName} invited {inviteeSummary(consultation)} to a Teams consultation
        · {formatEntryTime(consultation.startedAt)}
      </span>
    </div>
  );
}

/**
 * Muted "for the record" line for a cancelled/closed consultation, anchored at the moment it
 * ended. It says who ended it and keeps the way back to the conversation — the Teams deep link
 * and the lazy transcript — because stopping a consultation must not lose what was said in it.
 */
export function ConsultationClosedEntry({ consultation }: { consultation: ConsultationView }) {
  const [expanded, setExpanded] = useState(false);
  const when = consultation.endedAt ?? consultation.startedAt;
  const verb = consultation.displayStatus === "closed" ? "closed" : "cancelled";
  const actor = consultation.endedByName ? ` by ${consultation.endedByName}` : "";
  const replies = consultation.inboundReplyCount;
  const canExpand = Boolean(consultation.teamsChatId) && consultation.transcriptCount > 0;
  return (
    <div
      role="note"
      aria-label="Teams consultation ended"
      className="border-border flex flex-col gap-1.5 border-t px-4 py-2"
    >
      <div className="flex items-center gap-1.5">
        <TeamsIcon size={13} className="opacity-60" />
        <span className="text-[10px] text-muted-foreground/70">
          Teams consultation {verb}
          {actor} · {formatEntryTime(when)}
          {replies > 0 && ` · ${replies} ${replies === 1 ? "reply" : "replies"} received`}
        </span>
      </div>
      {(canExpand || consultation.webUrl) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 pl-[19px] text-[11px] font-medium">
          {canExpand && (
            <TranscriptToggle
              expanded={expanded}
              count={consultation.transcriptCount}
              onToggle={() => setExpanded((value) => !value)}
            />
          )}
          {consultation.webUrl && <OpenInTeamsLink webUrl={consultation.webUrl} />}
        </div>
      )}
      {canExpand && expanded && consultation.teamsChatId && (
        <ConsultationTranscript teamsChatId={consultation.teamsChatId} />
      )}
    </div>
  );
}

function statusCopy(consultation: ConsultationView): {
  body: string;
  note?: string;
  meta?: string;
} {
  const names = inviteeSummary(consultation);
  switch (consultation.displayStatus) {
    case "setting_up":
      return {
        body: `Setting up the Teams chat and inviting ${names}…`,
        note: "Usually ready in under a minute.",
      };
    case "waiting":
      return {
        body: `Waiting for ${names} to reply in Microsoft Teams.`,
        meta: "No replies yet",
      };
    case "in_progress": {
      const count = consultation.inboundReplyCount;
      // A reply can come from anyone in the chat — including the requester
      // jumping in — so never name who is "replying". State only the safe fact
      // (who is being consulted) and let the reply count convey the activity.
      return {
        body: `Consulting ${names} in Microsoft Teams.`,
        meta: `${count} ${count === 1 ? "reply" : "replies"} so far`,
      };
    }
    case "needs_attention":
      return {
        body: consultation.failureMessage ?? "Something went wrong with this Teams consultation.",
      };
    default:
      return { body: `Consulting ${names} in Microsoft Teams.` };
  }
}

/**
 * Light "Open in Teams" deep link (Pencil `msgOpen`) — a flat text link, not a
 * button, so it sits inline with the other entry actions on a single row.
 */
function OpenInTeamsLink({ webUrl }: { webUrl: string }) {
  return (
    <a
      href={webUrl}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 text-muted-foreground transition-colors hover:text-foreground"
    >
      <ExternalLink className="size-3" />
      Open in Teams
    </a>
  );
}

/**
 * "View transcript (N)" / "Hide transcript" toggle (Pencil cB1BB / e2b84L). The
 * caller owns the expanded state so it can place this link in the shared action
 * row and render the lazy <ConsultationTranscript> below it.
 */
function TranscriptToggle({
  expanded,
  count,
  onToggle,
}: {
  expanded: boolean;
  count: number;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      className="inline-flex items-center gap-1 text-[#6264A7] transition-colors hover:opacity-80"
    >
      {expanded ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
      {expanded ? "Hide transcript" : `View transcript (${count})`}
    </button>
  );
}

/**
 * Live status entry pinned to the bottom of the thread while a consultation is
 * not yet answered (Pencil nodes JqARq / cB1BB). Owns the retry/cancel actions;
 * Cancel is offered only to the viewer the backend says may cancel, and confirms
 * first because it tells everyone in the Teams chat that the conversation is over.
 */
export function ConsultationStatusEntry({ consultation }: { consultation: ConsultationView }) {
  const retry = useRetryConsultationSetup();
  const cancel = useCancelConsultation();
  const [expanded, setExpanded] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const consultationId = consultation.id as Id<"clarificationConsultations">;
  const { body, note, meta } = statusCopy(consultation);
  const isNeedsAttention = consultation.displayStatus === "needs_attention";
  const showRetry = isNeedsAttention && consultation.failureRetryable;
  const showOpenChat = Boolean(consultation.webUrl) && consultation.displayStatus !== "setting_up";
  const canExpand =
    consultation.displayStatus === "in_progress" &&
    Boolean(consultation.teamsChatId) &&
    consultation.transcriptCount > 0;

  return (
    <article
      aria-label={`Teams consultation ${consultation.topic}`}
      className="border-border border-t px-4 py-2.5"
    >
      <div className="mb-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1">
        <TeamsIcon size={16} />
        <span className="whitespace-nowrap text-foreground text-xs font-semibold">
          Microsoft Teams
        </span>
        <ConsultationStatusBadge displayStatus={consultation.displayStatus} />
        <time
          dateTime={consultation.startedAt.toISOString()}
          className="ml-auto whitespace-nowrap text-muted-foreground text-xs"
        >
          {formatEntryTime(consultation.startedAt)}
        </time>
      </div>
      <p className="text-foreground/80 text-[13px] leading-snug">{body}</p>
      {note && <p className="mt-0.5 text-[11px] italic text-muted-foreground">{note}</p>}
      {meta && <p className="mt-1 text-[11px] text-muted-foreground">{meta}</p>}
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] font-medium">
        {showRetry && (
          <button
            type="button"
            onClick={() => void retry({ consultationId })}
            className="inline-flex items-center gap-1 text-muted-foreground transition-colors hover:text-foreground"
          >
            <RotateCcw className="size-3" />
            Retry invite
          </button>
        )}
        {canExpand && (
          <TranscriptToggle
            expanded={expanded}
            count={consultation.transcriptCount}
            onToggle={() => setExpanded((value) => !value)}
          />
        )}
        {showOpenChat && consultation.webUrl && <OpenInTeamsLink webUrl={consultation.webUrl} />}
        {consultation.canCancel && (
          <button
            type="button"
            onClick={() => setConfirmingCancel(true)}
            className="inline-flex items-center gap-1 text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="size-3" />
            Cancel
          </button>
        )}
      </div>
      {canExpand && expanded && consultation.teamsChatId && (
        <ConsultationTranscript teamsChatId={consultation.teamsChatId} />
      )}
      {consultation.canCancel && (
        <ConfirmDialog
          open={confirmingCancel}
          onOpenChange={setConfirmingCancel}
          title="Cancel this Teams consultation?"
          description={`Claudia will let ${inviteeSummary(consultation)} know in Teams and stop following up. What was said so far stays available here.`}
          confirmLabel="Cancel consultation"
          pendingLabel="Cancelling…"
          cancelLabel="Keep it open"
          destructive
          onConfirm={async () => {
            try {
              await cancel({ consultationId });
            } catch (error) {
              toast.error(getErrorMessage(error, "Failed to cancel the Teams consultation."));
            } finally {
              setConfirmingCancel(false);
            }
          }}
        />
      )}
    </article>
  );
}

/**
 * Claudia's concluding summary, rendered richly with an "Answered" badge and an
 * expandable transcript (Pencil nodes e2b84L / AEShP). Anchored in the timeline
 * at the moment the summary message was posted.
 */
export function ConsultationSummaryEntry({
  consultation,
  content,
  timestamp,
}: {
  consultation: ConsultationView;
  content: string;
  timestamp: Date;
}) {
  const [expanded, setExpanded] = useState(false);
  const canExpand = Boolean(consultation.teamsChatId) && consultation.transcriptCount > 0;

  return (
    <article aria-label="Teams consultation summary" className="border-border border-t px-4 py-2.5">
      <div className="mb-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1">
        <TeamsIcon size={16} />
        <span className="whitespace-nowrap text-foreground text-xs font-semibold">
          Microsoft Teams
        </span>
        <ConsultationStatusBadge displayStatus="answered" />
        <time
          dateTime={timestamp.toISOString()}
          className="ml-auto whitespace-nowrap text-muted-foreground text-xs"
        >
          {formatEntryTime(timestamp)}
        </time>
      </div>
      <MessageMarkdown content={content} className="text-foreground/80 text-xs" />
      {(canExpand || consultation.webUrl) && (
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] font-medium">
          {canExpand && (
            <TranscriptToggle
              expanded={expanded}
              count={consultation.transcriptCount}
              onToggle={() => setExpanded((value) => !value)}
            />
          )}
          {consultation.webUrl && <OpenInTeamsLink webUrl={consultation.webUrl} />}
        </div>
      )}
      {canExpand && expanded && consultation.teamsChatId && (
        <ConsultationTranscript teamsChatId={consultation.teamsChatId} />
      )}
    </article>
  );
}
